/** CP-060 GAP-P1-14 — Route Create source flow helpers (testable, no UI). */

export type SuccessorRouteCreateContext = {
  createContext?: 'SUCCESSOR_ROUTE';
  predecessorRouteId?: number;
  successorControlTaskId?: number;
  applicationId?: number;
  warehouseId?: number;
};

export function parseSuccessorRouteCreateSearchParams(
  params: URLSearchParams
): SuccessorRouteCreateContext {
  const createContext = params.get('createContext');
  const predecessorRouteId = num(params.get('predecessorRouteId'));
  const successorControlTaskId = num(params.get('successorControlTaskId'));
  const applicationId = num(params.get('applicationId'));
  const warehouseId = num(params.get('warehouseId'));
  return {
    ...(createContext === 'SUCCESSOR_ROUTE' ? { createContext: 'SUCCESSOR_ROUTE' as const } : {}),
    ...(predecessorRouteId != null ? { predecessorRouteId } : {}),
    ...(successorControlTaskId != null ? { successorControlTaskId } : {}),
    ...(applicationId != null ? { applicationId } : {}),
    ...(warehouseId != null ? { warehouseId } : {})
  };
}

function num(raw: string | null): number | undefined {
  if (raw == null || raw === '') return undefined;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : undefined;
}

/** RC-3 / CP-059: warehouse cargo availability query args. */
export function buildWarehouseAvailableCargoQuery(warehouseId: number, excludeRouteId?: number) {
  return {
    loadingPointType: 'warehouse' as const,
    loadingPointId: warehouseId,
    ...(excludeRouteId != null ? { excludeRouteId } : {})
  };
}

/** RC-1: application must be selected before cargo pick (step gate). */
export function assertApplicationSelectedBeforeCargo(applicationId: number | null | undefined): boolean {
  return applicationId != null && Number.isFinite(Number(applicationId)) && Number(applicationId) > 0;
}

/** RC-2: physical source is application (По заявке) or warehouse (Со склада). */
export type LoadingPhysicalSource = 'application' | 'warehouse';

export function resolveLoadingPhysicalSource(
  loadingPointType: 'application' | 'warehouse' | undefined
): LoadingPhysicalSource | null {
  if (loadingPointType === 'application') return 'application';
  if (loadingPointType === 'warehouse') return 'warehouse';
  return null;
}

/** RC-4: created loading stop must bind warehouse as physical source. */
export function buildWarehouseLoadingStopPayload(warehouseId: number) {
  return {
    type: 'loading' as const,
    pointType: 'warehouse' as const,
    pointId: warehouseId
  };
}

/** RC-5 acceptance: given CP-059 projection, available qty is source-specific (not total). */
export function pickAvailableQuantityFromCargoRow(row: {
  quantity?: number;
  availableQuantity?: number;
}): number {
  if (row.availableQuantity != null && Number.isFinite(Number(row.availableQuantity))) {
    return Number(row.availableQuantity);
  }
  return Number(row.quantity ?? 0);
}

/** Attach CP-060 successor Route Create fields to POST /routes body when context is complete. */
export function mergeSuccessorContextIntoCreateRouteBody<T extends object>(
  routeBody: T,
  ctx: SuccessorRouteCreateContext | undefined
): T & Partial<SuccessorRouteCreateContext> {
  if (ctx?.createContext !== 'SUCCESSOR_ROUTE') {
    return routeBody;
  }
  if (ctx.predecessorRouteId == null || ctx.successorControlTaskId == null) {
    return routeBody;
  }
  return {
    ...routeBody,
    createContext: 'SUCCESSOR_ROUTE',
    predecessorRouteId: ctx.predecessorRouteId,
    successorControlTaskId: ctx.successorControlTaskId
  };
}
